import { FieldGrid } from '../form/FieldGrid';
import { CurrencyField } from '../form/CurrencyField';
import { DerivedStat } from './DerivedStat';
import type { UseIndicationFormResult } from '../../hooks/useIndicationForm';
import { formatCurrency } from '../../lib/format';

interface Props {
  form: UseIndicationFormResult;
}

/**
 * CurrencyField stores a raw number on every keystroke. We treat 0 as
 * "present with value 0" only when the user has touched the field; empty
 * rendering stays as undefined in state so zod's required rule fires.
 */
export function StepBudget({ form }: Props) {
  const { state, setField, markTouched, errors } = form;

  const totalTiv =
    (state.hardCosts ?? 0) +
    (state.softCosts ?? 0) +
    (state.lossOfBusinessIncome ?? 0);

  return (
    <fieldset className="border-0 p-0 m-0">
      <legend className="sr-only">Construction budget</legend>
      <FieldGrid>
        <div data-field="hardCosts">
          <CurrencyField
            label="Hard Costs"
            required
            value={state.hardCosts}
            onValueChange={(v) => setField('hardCosts', v === 0 ? undefined : v)}
            onBlur={() => markTouched('hardCosts')}
            error={errors.hardCosts}
          />
        </div>

        <div data-field="softCosts">
          <CurrencyField
            label="Soft Costs"
            required
            value={state.softCosts}
            onValueChange={(v) => setField('softCosts', v)}
            onBlur={() => markTouched('softCosts')}
            error={errors.softCosts}
          />
        </div>

        <div data-field="lossOfBusinessIncome" className="md:col-span-2">
          <CurrencyField
            label="Loss of Business Income (optional)"
            value={state.lossOfBusinessIncome}
            onValueChange={(v) => setField('lossOfBusinessIncome', v)}
            onBlur={() => markTouched('lossOfBusinessIncome')}
            error={errors.lossOfBusinessIncome}
            hint="If unsure, leave at $0. Typically equals projected annual rent loss during construction."
          />
        </div>
      </FieldGrid>

      <div className="mt-10">
        <DerivedStat
          label="Total Construction Budget"
          serif
          value={formatCurrency(totalTiv)}
        />
      </div>
    </fieldset>
  );
}
